/**
 * Helper functions for AI onboarding data transformation and validation
 */

import api from '../api/axios';

const normalizeRoleValue = (value = '') => {
    const text = String(value || '').trim();
    if (!text) return '';

    const normalized = text.toLowerCase().replace(/\s+/g, ' ').trim();
    const blockedPhrases = [
        'keep current role',
        'add another target role',
        'change role preference',
        'keep my current role',
        'i want to add another target role',
        'i want to change my role preference'
    ];

    if (blockedPhrases.includes(normalized)) {
        return '';
    }

    return text;
};

export const mapAiChatToProfileData = (aiPayload = {}) => {
    const safePayload = aiPayload && typeof aiPayload === 'object' ? aiPayload : {};
    const role = normalizeRoleValue(safePayload.desiredRole || safePayload.role || '');
    const goals = Array.isArray(safePayload.goals) ? safePayload.goals : [];
    const desiredRoles = Array.isArray(safePayload.desiredRoles) ? safePayload.desiredRoles : (role ? [role] : []);

    return {
        username: safePayload.username || safePayload.fullName || '',
        desiredRole: role,
        desiredRoles,
        experienceLevel: safePayload.experienceLevel || safePayload.seniority || '',
        skills: Array.isArray(safePayload.skills) ? safePayload.skills : [],
        locationPreference: safePayload.locationPreference || safePayload.location || '',
        preferredIndustries: Array.isArray(safePayload.preferredIndustries)
            ? safePayload.preferredIndustries
            : (Array.isArray(safePayload.industries) ? safePayload.industries : []),
        preferredIndustry: Array.isArray(safePayload.preferredIndustry)
            ? safePayload.preferredIndustry
            : (Array.isArray(safePayload.preferredIndustries) ? safePayload.preferredIndustries : (Array.isArray(safePayload.industries) ? safePayload.industries : [])),
        preferredDepartments: Array.isArray(safePayload.preferredDepartments)
            ? safePayload.preferredDepartments
            : (Array.isArray(safePayload.departments) ? safePayload.departments : []),
        preferredDepartment: Array.isArray(safePayload.preferredDepartment)
            ? safePayload.preferredDepartment
            : (Array.isArray(safePayload.preferredDepartments) ? safePayload.preferredDepartments : (Array.isArray(safePayload.departments) ? safePayload.departments : [])),
        workModes: Array.isArray(safePayload.workModes) ? safePayload.workModes : [],
        careerGoal: safePayload.careerGoal || goals[0] || '',
        goals
    };
};

/** `requireSkills` defaults true (AI chat); set false for structured profile form where skills are optional. */
export const isAiProfileValid = (profile = {}, options = {}) => {
    const { requireSkills = true } = options;
    const safeProfile = profile && typeof profile === 'object' ? profile : {};
    const hasRole = Boolean(safeProfile.desiredRole);
    const hasExperience = Boolean(safeProfile.experienceLevel);
    const hasSkills = Array.isArray(safeProfile.skills) && safeProfile.skills.length > 0;
    const hasLocation = Boolean(safeProfile.locationPreference);
    const skillsOk = !requireSkills || hasSkills;
    return hasRole && hasExperience && hasLocation && skillsOk;
};

export const persistAiProfileData = async ({ sessionId, mappedProfile }) => {
    return api.post('/api/onboarding/session/complete', {
        sessionId,
        profileData: mappedProfile
    });
};

/**
 * Maps adaptive onboarding recap (API `recap`) to `profileData` for POST /session/complete.
 * Keeps Settings › Onboarding in sync with chat-collected preferences when the LLM gate skips updates.
 */
export const mapSessionRecapToProfileData = (recap = {}) => {
    const r = recap && typeof recap === 'object' ? recap : {};
    const desiredRoles =
        Array.isArray(r.roles) && r.roles.length > 0
            ? r.roles.map((x) => String(x || '').trim()).filter(Boolean)
            : r.role
              ? [String(r.role).trim()].filter(Boolean)
              : [];

    const desiredRoleDisplay =
        desiredRoles.length > 0 ? desiredRoles.join(' / ') : String(r.role || '').trim();

    return {
        desiredRole: desiredRoleDisplay,
        desiredRoles,
        experienceLevel: (() => {
            const s = String(r.seniority || '').toLowerCase();
            if (s.includes('entry') || s.includes('junior')) return 'entry';
            if (s.includes('senior')) return 'senior';
            if (s.includes('lead') || s.includes('principal') || s.includes('staff')) return 'lead';
            if (s.includes('mid') || s.includes('middle')) return 'mid';
            return '';
        })(),
        locationPreference: r.workMode === 'remote' ? 'Remote' : '',
        skills: [],
        industryPreference: Array.isArray(r.industries) ? r.industries.slice(0, 2).join(', ') : '',
        preferredIndustries: Array.isArray(r.industries) ? r.industries : [],
        preferredIndustry: Array.isArray(r.industries) ? r.industries : [],
        preferredDepartments: Array.isArray(r.departments) ? r.departments : [],
        preferredDepartment: Array.isArray(r.departments) ? r.departments : [],
        remotePreference: r.workMode || '',
        workModes: Array.isArray(r.workModes) ? r.workModes : [],
        salaryExpectation:
            r.compensation?.max != null
                ? String(r.compensation.max)
                : r.compensation?.min != null
                  ? String(r.compensation.min)
                  : ''
    };
};
